import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { csAdminApi } from '../../../services/api';
import { ACTIVE_GREEN } from '../../../constants/statusColors';
import {
  Paper,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  List,
  ListItem,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  DescriptionOutlined as DescriptionIcon,
  VisibilityOutlined as VisibilityIcon,
  DownloadOutlined as DownloadIcon,
  UploadOutlined as UploadIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';
import CompanyDocumentUploadModal from '../../../components/companies/CompanyDocumentUploadModal';
import DocumentViewerModal from '../../../components/common/DocumentViewerModal';
import { useLookups } from '../../../hooks/useLookups';
import {
  DEFAULT_MANDATORY_TYPE_CODES,
  partitionDocumentsByMandatory,
  dedupeDocuments,
  resolveMandatoryTypeCodes,
} from '../../../utils/mandatoryDocumentTypes';
import {
  mapCompanyApiDocument,
  toDocumentViewerModel,
  resolveCompanyDocumentFileUrl,
} from '../../../utils/companyDocumentMappers';
import { getSecureDocumentUrl } from '../../../utils/secureFileAccess';

const DocumentsTab = ({ companyId, company, onDocumentsChange }) => {
  const { items: lookupDocumentTypes, loading: lookupsLoading } = useLookups('COMPANY_DOCUMENT_TYPE');
  const [documents, setDocuments] = useState([]);
  const [mandatoryTypeCodes, setMandatoryTypeCodes] = useState([]);
  const [pageReady, setPageReady] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewerModalOpen, setViewerModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadPreset, setUploadPreset] = useState(null);
  const hasLoadedOnce = useRef(false);
  const lastReportedCount = useRef(null);
  const onDocumentsChangeRef = useRef(onDocumentsChange);
  onDocumentsChangeRef.current = onDocumentsChange;
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const reportDocumentCount = useCallback((count) => {
    if (lastReportedCount.current === count) return;
    lastReportedCount.current = count;
    onDocumentsChangeRef.current?.(count);
  }, []);

  const loadPageData = useCallback(async ({ silent = false } = {}) => {
    if (lookupsLoading) return;

    if (!silent && !hasLoadedOnce.current) {
      setPageReady(false);
    }

    if (!companyId) {
      setMandatoryTypeCodes([]);
      setDocuments([]);
      reportDocumentCount(0);
      setPageReady(true);
      hasLoadedOnce.current = true;
      return;
    }

    try {
      const [docsRes, settingsRes] = await Promise.all([
        csAdminApi.getCompanyDocuments(companyId),
        csAdminApi.getCompanySettings(companyId),
      ]);
      const documentsSettings = settingsRes.data?.data?.documents || {};
      setMandatoryTypeCodes(
        resolveMandatoryTypeCodes(documentsSettings, lookupDocumentTypes)
      );
      const mapped = dedupeDocuments(
        (docsRes.data?.data || []).map((doc, idx) => mapCompanyApiDocument(doc, lookupDocumentTypes, idx))
      );
      setDocuments(mapped);
      reportDocumentCount(mapped.length);
    } catch (err) {
      console.error('Company documents API:', err);
      setMandatoryTypeCodes([...DEFAULT_MANDATORY_TYPE_CODES]);
      setDocuments([]);
      reportDocumentCount(0);
    } finally {
      setPageReady(true);
      hasLoadedOnce.current = true;
    }
  }, [companyId, lookupsLoading, lookupDocumentTypes, reportDocumentCount]);

  useEffect(() => {
    lastReportedCount.current = null;
    hasLoadedOnce.current = false;
  }, [companyId]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const { documentSlots, otherDocuments } = useMemo(
    () => partitionDocumentsByMandatory(documents, mandatoryTypeCodes, lookupDocumentTypes),
    [documents, mandatoryTypeCodes, lookupDocumentTypes]
  );

  const handleOpenUpload = (slot) => {
    setUploadPreset({ label: slot.typeLabel, code: slot.typeCode });
    setUploadModalOpen(true);
  };

  const handleOpenOtherUpload = () => {
    setUploadPreset(null);
    setUploadModalOpen(true);
  };

  const handleUploadDocument = () => {
    loadPageData({ silent: true });
    setSnackbar({ open: true, message: 'Document uploaded successfully', severity: 'success' });
    setUploadPreset(null);
  };

  const handleUpdateDocument = async () => {
    await loadPageData({ silent: true });
    setSnackbar({ open: true, message: 'Document updated successfully', severity: 'success' });
    setEditModalOpen(false);
    setSelectedDocument(null);
  };

  const handleEditDocument = (doc) => {
    setSelectedDocument({ ...doc });
    setEditModalOpen(true);
  };

  const handleViewDocument = (doc) => {
    setSelectedDocument(toDocumentViewerModel(doc, lookupDocumentTypes));
    setViewerModalOpen(true);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const url = await getSecureDocumentUrl(doc.fileUrl);
      if (!url) {
        setSnackbar({ open: true, message: 'Download URL not available', severity: 'warning' });
        return;
      }
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.fileName || doc.title || 'document';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to download document:', err);
      setSnackbar({ open: true, message: 'Failed to download document', severity: 'error' });
    }
  };

  const canEditDocument = (doc) => doc?.source === 'company' || doc?.source === 'cs';

  const handleDeleteDocument = useCallback(async (id) => {
    const docId = id ?? selectedDocument?.documentId ?? selectedDocument?.id;
    if (!docId || String(docId).startsWith('row-')) {
      setSnackbar({ open: true, message: 'Cannot delete this document', severity: 'error' });
      return;
    }
    if (selectedDocument && !canEditDocument(selectedDocument)) {
      setSnackbar({ open: true, message: 'This document cannot be deleted', severity: 'warning' });
      return;
    }

    try {
      {
        await csAdminApi.deleteDocument(docId);
      }
      await loadPageData({ silent: true });
      setSnackbar({ open: true, message: 'Document deleted successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to delete company document:', err);
      const message = err.response?.data?.error?.message || 'Failed to delete document';
      setSnackbar({ open: true, message, severity: 'error' });
      throw err;
    }
  }, [selectedDocument, loadPageData]);

  const handleReplaceDocument = useCallback(() => {
    if (selectedDocument && !canEditDocument(selectedDocument)) {
      setSnackbar({ open: true, message: 'This document cannot be replaced', severity: 'warning' });
      return;
    }
    const docId = selectedDocument?.documentId ?? selectedDocument?.id;
    const original = documents.find((d) => (d.documentId ?? d.id) === docId) || selectedDocument;
    setViewerModalOpen(false);
    setSelectedDocument({ ...original });
    setEditModalOpen(true);
  }, [selectedDocument, documents]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB');
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return 'No Expiry';
    const daysUntil = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return 'Expired';
    if (daysUntil <= 30) return 'Expiring Soon';
    return 'Valid';
  };

  const renderDocumentActions = (doc) => (
    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
      <IconButton
        size="small"
        title={canEditDocument(doc) ? 'Edit' : 'Edit (CS uploads only)'}
        onClick={() => handleEditDocument(doc)}
        disabled={!canEditDocument(doc)}
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" title="View" onClick={() => handleViewDocument(doc)}>
        <VisibilityIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" title="Download" onClick={() => handleDownloadDocument(doc)}>
        <DownloadIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Company Documents</Typography>
        <Button variant="outlined" startIcon={<UploadIcon />} size="small" onClick={handleOpenOtherUpload}>
          Upload other document
        </Button>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Required types are configured in Settings → Documents (from the Company Document Types lookup).
      </Typography>

      {!pageReady ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
      <>
      <List sx={{ p: 0, mb: 3 }}>
        {documentSlots.map((slot, index) => (
          <ListItem
            key={slot.typeCode}
            sx={{
              px: 0,
              py: 1.5,
              borderBottom: index < documentSlots.length - 1 ? '1px solid #E0E0E0' : 'none',
              alignItems: 'flex-start',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
              <DescriptionIcon sx={{ mt: 0.5, color: slot.uploaded ? ACTIVE_GREEN : 'text.disabled' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" fontWeight={500}>{slot.typeLabel}</Typography>
                {slot.uploaded ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {slot.uploaded.typeLabel || slot.uploaded.title || slot.uploaded.fileName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                      {slot.uploaded.number && (
                        <Chip 
                          label={`ID: ${slot.uploaded.number}`} 
                          size="small" 
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }} 
                        />
                      )}
                      {slot.uploaded.expiryDate && (
                        <Chip 
                          label={`Exp: ${formatDate(slot.uploaded.expiryDate)}`} 
                          size="small" 
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }} 
                        />
                      )}
                      <Chip 
                        label={getExpiryStatus(slot.uploaded.expiryDate)} 
                        size="small" 
                        color={
                          getExpiryStatus(slot.uploaded.expiryDate) === 'Expired' || 
                          getExpiryStatus(slot.uploaded.expiryDate) === 'Expiring Soon' 
                            ? 'error' 
                            : getExpiryStatus(slot.uploaded.expiryDate) === 'Valid' 
                            ? 'success' 
                            : 'default'
                        }
                        sx={{ height: 20, fontSize: '0.7rem' }} 
                      />
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="caption" color="textSecondary">Not uploaded</Typography>
                )}
              </Box>
              {slot.uploaded ? (
                renderDocumentActions(slot.uploaded)
              ) : (
                <Button size="small" variant="contained" startIcon={<UploadIcon />} onClick={() => handleOpenUpload(slot)}>
                  Upload
                </Button>
              )}
            </Box>
          </ListItem>
        ))}
      </List>

      {otherDocuments.length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Other documents</Typography>
          <List sx={{ p: 0 }}>
            {otherDocuments.map((doc, index) => (
              <ListItem
                key={doc.id}
                sx={{
                  px: 0,
                  py: 2,
                  borderBottom: index < otherDocuments.length - 1 ? '1px solid #E0E0E0' : 'none',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
                  <DescriptionIcon sx={{ color: '#E65100', mt: 0.5 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={500}>{doc.typeLabel || doc.type || doc.title}</Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {doc.title || doc.fileName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                      {doc.number && (
                        <Chip 
                          label={`ID: ${doc.number}`} 
                          size="small" 
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }} 
                        />
                      )}
                      {doc.expiryDate && (
                        <Chip 
                          label={`Exp: ${formatDate(doc.expiryDate)}`} 
                          size="small" 
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }} 
                        />
                      )}
                      {doc.expiryDate && (
                        <Chip 
                          label={getExpiryStatus(doc.expiryDate)} 
                          size="small" 
                          color={
                            getExpiryStatus(doc.expiryDate) === 'Expired' || 
                            getExpiryStatus(doc.expiryDate) === 'Expiring Soon' 
                              ? 'error' 
                              : getExpiryStatus(doc.expiryDate) === 'Valid' 
                              ? 'success' 
                              : 'default'
                          }
                          sx={{ height: 20, fontSize: '0.7rem' }} 
                        />
                      )}
                    </Box>
                  </Box>
                  {renderDocumentActions(doc)}
                </Box>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {documentSlots.length === 0 && otherDocuments.length === 0 && (
        <Box textAlign="center" py={4}>
          <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
            No document types configured
          </Typography>
        </Box>
      )}
      </>
      )}

      <CompanyDocumentUploadModal
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setUploadPreset(null);
        }}
        onUpload={handleUploadDocument}
        companyId={companyId}
        companyName={company?.name || 'Company'}
        mode="create"
        presetDocumentType={uploadPreset}
      />

      <CompanyDocumentUploadModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedDocument(null);
        }}
        onUpdate={handleUpdateDocument}
        companyId={companyId}
        companyName={company?.name || 'Company'}
        document={selectedDocument}
        mode="edit"
      />

      {selectedDocument && (
        <DocumentViewerModal
          open={viewerModalOpen}
          onClose={() => {
            setViewerModalOpen(false);
            setSelectedDocument(null);
          }}
          document={selectedDocument}
          onDownload={handleDownloadDocument}
          onDelete={handleDeleteDocument}
          onReplace={handleReplaceDocument}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default DocumentsTab;
